<template>
  <div class="deals-page bg-background">

    <!-- HERO -->
    <v-container fluid class="hero-section py-16 text-center">
      <h1 class="hero-title">
        Current Deals
      </h1>
      <p class="hero-sub mt-6">
          Find and book your next vacation
        </p>
    </v-container>

    <v-container fluid class="py-10">
      <v-container max-width="1400">
        <v-row>
          <v-col cols="12" md="12">
            <DealsFiltersBar v-model="filters" :port-items="portItems" :ship-items="shipItems" :destination-items="[]"
              :nights-min="nightsMin" :nights-max="nightsMax" />

            <!-- Active chips -->
            <div v-if="hasActiveFilters" class="mb-3">
              <v-chip v-for="chip in activeFilterChips" :key="chip.key + String(chip.value)" closable class="me-2 mb-2"
                color="primary" @click:close="removeChip(chip)">
                {{ chip.label }}
              </v-chip>
            </div>

            <!-- Loading -->
            <template v-if="isLoading">
              <v-skeleton-loader v-for="i in itemsPerPage" :key="i" type="card" class="mb-4" elevation="2" rounded="lg"
                boilerplate loading />
            </template>

            <!-- Listings -->
            <template v-else>
              <div v-if="totalCount">
                <div v-for="cruise in pagedResults" :key="cruise.id" class="mb-4">
                  <CruiseListingCard :cruise="cruise" />
                </div>

                <!-- Pagination bar -->
                <v-row class="mt-2" align="center" justify="space-between">
                  <v-col cols="auto">
                    <small class="text-medium-emphasis">
                      Showing {{ pageStart + 1 }}–{{ pageEnd }} of {{ totalCount }}
                    </small>
                  </v-col>
                  <v-col cols="auto" class="d-flex align-center ga-4">
                    <v-select v-model="itemsPerPage" :items="[5, 10, 12, 16, 24]" label="Rows" hide-details
                      density="compact" variant="outlined" style="max-width: 120px" />
                    <v-pagination v-model="page" :length="totalPages" :total-visible="7" density="comfortable"
                      prev-icon="mdi-chevron-left" next-icon="mdi-chevron-right" @update:modelValue="scrollToTop" />
                  </v-col>
                </v-row>
              </div>

              <v-alert v-else type="info" variant="tonal" class="ma-2">
                No listings match your filters.
              </v-alert>
            </template>
          </v-col>
        </v-row>
      </v-container>
    </v-container>
  </div>
</template>

<script>
import { supabase } from '@/supabaseClient'
import CruiseListingCard from '@/components/CruiseListingCard.vue'
import DealsFiltersBar from '@/components/DealsFiltersBar.vue'

export default {
  name: 'DealsPage',
  components: { DealsFiltersBar, CruiseListingCard },

  data() {
    return {
      loading: true,
      loadToken: 0,          // <— guards overlapping loads
      listings: [],
      uniquePorts: [],
      uniqueShips: [],
      // pagination
      page: 1,
      itemsPerPage: 12,

      // Filters
      filters: {
        search: '',
        ports: [],
        ships: [],
        months: [],
        nights: [],
        dateRange: null,
        sort: 'date-asc'
      },
      sortItems: [
        { title: 'Date — Soonest', value: 'date-asc' },
        { title: 'Nights — Shortest', value: 'nights-asc' },
        { title: 'Title — A → Z', value: 'title-asc' }
      ]
    }
  },

  computed: {
    isLoading() { return this.loading },

    uniqueNights() {
      const arr = this.listings
        .map(item => {
          const m = item.title?.match(/(\d+)\s*days?/i)
          return m ? parseInt(m[1], 10) : null
        })
        .filter(n => n != null)
      return [...new Set(arr)].sort((a, b) => a - b)
    },
    nightsMin() { return this.uniqueNights[0] ?? 1 },
    nightsMax() { return this.uniqueNights[this.uniqueNights.length - 1] ?? 20 },

    portItems() {
      return this.uniquePorts.map(p => ({ title: p || 'Unknown', value: p || 'Unknown' }))
    },
    shipItems() {
      return this.uniqueShips.map(s => ({ title: s || 'Unknown', value: s || 'Unknown' }))
    },
    hasNightsFilter() {
      const n = this.filters.nights
      if (!Array.isArray(n)) return false
      return n[0] > this.nightsMin || n[1] < this.nightsMax
    },

    activeFilterChips() {
      const chips = []
      if (this.filters.search)
        chips.push({ key: 'search', value: this.filters.search, label: `Search: ${this.filters.search}` })
      this.filters.ports.forEach(p => chips.push({ key: 'port', value: p, label: `Port: ${p}` }))
      this.filters.ships.forEach(s => chips.push({ key: 'ship', value: s, label: `Ship: ${s}` }))
      if (this.hasNightsFilter) {
        const [a, b] = this.filters.nights
        chips.push({ key: 'nights', value: [a, b], label: `Nights: ${a}–${b}` })
      }
      if (this.filters.dateRange?.length === 2) {
        const [a, b] = this.filters.dateRange
        chips.push({ key: 'dateRange', value: [a, b], label: `Dates: ${this.prettyDate(a)} – ${this.prettyDate(b)}` })
      }
      return chips
    },
    hasActiveFilters() { return this.activeFilterChips.length > 0 },

    filteredAndSorted() {
      const search = this.filters.search.trim().toLowerCase()
      const portSet = new Set(this.filters.ports)
      const shipSet = new Set(this.filters.ships)
      const [minN, maxN] = this.filters.nights
      const hasDate = Array.isArray(this.filters.dateRange) && this.filters.dateRange.length === 2
      const start = hasDate ? new Date(this.filters.dateRange[0]).setHours(0, 0, 0, 0) : null
      const end = hasDate ? new Date(this.filters.dateRange[1]).setHours(23, 59, 59, 999) : null
      const getNights = (t) => {
        const m = t?.match(/(\d+)\s*days?/i)
        return m ? parseInt(m[1], 10) : null
      }
      const getDateMs = (d) => d ? new Date(d).getTime() : NaN

      let out = this.listings.filter(item => {
        if (search) {
          const hay = `${item.title || ''} ${item.departure_port || ''} ${item.ship_name || ''}`.toLowerCase()
          if (!hay.includes(search)) return false
        }

        // --- PORT FILTER: flexible match, no change to listings data ---
        if (portSet.size) {
          const itemPort = (item.departure_port || 'Unknown').toLowerCase()

          let portMatch = false
          for (const selectedPort of portSet) {
            const p = (selectedPort || '').toLowerCase()
            // match either direction: "fort lauderdale" vs "fort lauderdale, florida"
            if (itemPort.includes(p) || p.includes(itemPort)) {
              portMatch = true
              break
            }
          }
          if (!portMatch) return false
        }

        if (shipSet.size && !shipSet.has(item.ship_name || 'Unknown')) return false
        const n = getNights(item.title)
        if (n != null && (n < minN || n > maxN)) return false
        if (hasDate) {
          const t = getDateMs(item.date)
          if (isNaN(t) || t < start || t > end) return false
        }
        return true
      })

      switch (this.filters.sort) {
        case 'date-asc':
          out.sort((a, b) => (new Date(a.date) - new Date(b.date)))
          break
        case 'nights-asc':
          out.sort((a, b) => (getNights(a.title) || 999) - (getNights(b.title) || 999))
          break
        case 'title-asc':
          out.sort((a, b) => (a.title || '').localeCompare(b.title || ''))
          break
      }
      return out
    },

    // pagination computations
    totalCount() { return this.filteredAndSorted.length },
    totalPages() { return Math.max(1, Math.ceil(this.totalCount / this.itemsPerPage)) },
    pageStart() { return (this.page - 1) * this.itemsPerPage },
    pageEnd() { return Math.min(this.pageStart + this.itemsPerPage, this.totalCount) },
    pagedResults() { return this.filteredAndSorted.slice(this.pageStart, this.pageEnd) }
  },
  activated() {
    // comment out if you don’t use keep-alive
    this.refresh()
  },
  async created() {
    await this.refresh()
  },
  mounted() {
    this.fetchDeparturePorts()
  },

  watch: {
    // whenever filters change, reset to page 1 and clamp
    filters: {
      handler() {
        this.page = 1
        if (this.page > this.totalPages) this.page = this.totalPages
      },
      deep: true
    },
    itemsPerPage() {
      if (this.page > this.totalPages) this.page = this.totalPages
    }
  },

  methods: {
    scrollToTop() {
      // nice UX on page change (optional)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    prettyDate(d) {
      try { return new Date(d).toLocaleDateString() } catch { return '' }
    },
    removeChip(chip) {
      if (chip.key === 'search') this.filters.search = ''
      if (chip.key === 'port') this.filters.ports = this.filters.ports.filter(p => p !== chip.value)
      if (chip.key === 'ship') this.filters.ships = this.filters.ships.filter(s => s !== chip.value)
      if (chip.key === 'nights') this.filters.nights = [this.nightsMin, this.nightsMax]
      if (chip.key === 'dateRange') this.filters.dateRange = null
    },
    clearFilters() {
      this.filters.search = ''
      this.filters.ports = []
      this.filters.ships = []
      this.filters.nights = [this.nightsMin, this.nightsMax]
      this.filters.dateRange = null
      this.filters.sort = 'date-asc'
    },
    async refresh() {
      const myToken = ++this.loadToken
      this.loading = true
      try {
        // Run both queries in parallel
        const [ok1, ok2] = await Promise.allSettled([
          this.fetchItineraries(),
          this.fetchDeparturePorts()
        ])

        // If another refresh started after us, bail
        if (myToken !== this.loadToken) return

        // Nights range depends on listings; set after data lands
        this.filters.nights = [this.nightsMin, this.nightsMax]

        // Clamp page now that counts are known
        if (this.page > this.totalPages) this.page = this.totalPages

        // Optional: log errors from either promise
        if (ok1.status === 'rejected') console.error(ok1.reason)
        if (ok2.status === 'rejected') console.error(ok2.reason)
      } finally {
        if (myToken === this.loadToken) this.loading = false
      }
    },

    async fetchDeparturePorts() {
      const { data, error } = await supabase.rpc('unique_departure_ports');
      if (error) throw error;

      const clean = s => {
        const p = String(s).split(',').map(t => t.trim()).filter(Boolean);
        return p.length >= 2 ? `${p[0]}, ${p[p.length - 1]}` : p[0];
      };

      this.uniquePorts = [...new Set((data || []).map(r => clean(r.port)))].sort();
    },

    async fetchItineraries() {
      // dates as YYYY-MM-DD (works for DATE columns)
      const today = new Date().toISOString().slice(0, 10)

      // 1) listings: current_step = 1 AND (remove_date IS NULL OR remove_date >= today)
      const { data: listings, error: listErr } = await supabase
        .from('listings')
        .select('sailing_id')
        .eq('current_step', 1)
        .or(`remove_date.is.null,remove_date.gte.${today}`)
      if (listErr) throw listErr

      const ids = [...new Set((listings ?? []).map(l => l.sailing_id).filter(Boolean))]
      if (!ids.length) { this.listings = []; return }

      // 2) cruises that haven't passed yet
      const { data: cruises, error: cruiseErr } = await supabase
        .from('cruises')
        .select('*')
        .in('id', ids)
        .gte('date', today)                  // if 'date' is a DATE. If TIMESTAMP, use start-of-day ISO
        .order('date', { ascending: true })
      if (cruiseErr) throw cruiseErr

      this.listings = (cruises ?? []).map(i => ({ id: i.id, ...i }))
    }

  }
}
</script>

<style scoped>
.deals-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
</style>
